import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Grid, Paper, Skeleton,
} from '@mui/material';
import { Add, LocalDining, Star, CheckCircle, MenuBook } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useRecipes } from '../../contexts/RecipeContext';
import RecipeCard from '../recipes/RecipeCard';

function StatCard({ icon, label, value, color }) {
  return (
    <Paper sx={{ p: 2, textAlign: 'center', flex: 1 }}>
      <Box sx={{ color, mb: 0.5 }}>{icon}</Box>
      <Typography variant="h5" sx={{ fontWeight: 800, color }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Paper>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { recipes, recipesLoading } = useRecipes();

  const firstName = user?.displayName?.split(' ')[0] || user?.email?.split('@')[0] || 'Chef';
  const starredRecipes = recipes.filter((r) => r.starred);
  const madeRecipes = recipes.filter((r) => r.status === 'made');
  const recentRecipes = recipes.slice(0, 6);

  if (recipesLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width={200} height={40} />
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          {[1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={80} sx={{ flex: 1 }} />)}
        </Box>
        <Skeleton variant="text" width={150} height={32} sx={{ mt: 3 }} />
        <Grid container spacing={2} sx={{ mt: 0 }}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rounded" height={280} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Greeting */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ mb: 0.5 }}>
          Hello, {firstName} 👋
        </Typography>
        <Typography color="text.secondary">
          What are we cooking today?
        </Typography>
      </Box>

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
        <StatCard icon={<MenuBook />} label="Total Recipes" value={recipes.length} color="primary.main" />
        <StatCard icon={<CheckCircle />} label="Made" value={madeRecipes.length} color="success.main" />
        <StatCard icon={<Star />} label="Starred" value={starredRecipes.length} color="warning.main" />
      </Box>

      {/* Quick actions */}
      <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/recipes/new')}
          sx={{
            flex: 1,
            py: 1.5,
            background: 'linear-gradient(135deg, #f0873c, #c96820)',
            '&:hover': { background: 'linear-gradient(135deg, #f5a76c, #f0873c)' },
          }}
        >
          Add Recipe
        </Button>
        <Button
          variant="outlined"
          startIcon={<LocalDining />}
          onClick={() => navigate('/cook')}
          sx={{ flex: 1, py: 1.5, borderColor: 'rgba(255,255,255,0.15)' }}
        >
          What Can I Cook?
        </Button>
      </Box>

      {recipes.length === 0 ? (
        /* Empty state */
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            background: 'linear-gradient(135deg, rgba(240,135,60,0.05), rgba(107,176,122,0.05))',
          }}
        >
          <Typography sx={{ fontSize: '4rem', mb: 2 }}>📖</Typography>
          <Typography variant="h5" sx={{ mb: 1 }}>Your recipe book is empty</Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Start by adding your first recipe or importing one from a URL.
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            size="large"
            onClick={() => navigate('/recipes/new')}
            sx={{ background: 'linear-gradient(135deg, #f0873c, #c96820)' }}
          >
            Add Your First Recipe
          </Button>
        </Paper>
      ) : (
        <>
          {/* Starred recipes */}
          {starredRecipes.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">⭐ Starred Recipes</Typography>
                <Button
                  size="small"
                  onClick={() => navigate('/recipes')}
                  sx={{ color: 'primary.main' }}
                >
                  View all
                </Button>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  overflowX: 'auto',
                  pb: 1,
                  '&::-webkit-scrollbar': { height: 4 },
                  '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 },
                }}
              >
                {starredRecipes.map((recipe) => (
                  <Box key={recipe.id} sx={{ minWidth: 260, maxWidth: 280 }}>
                    <RecipeCard recipe={recipe} />
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Recently added */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">🕐 Recently Added</Typography>
              <Button size="small" onClick={() => navigate('/recipes')} sx={{ color: 'primary.main' }}>
                View all
              </Button>
            </Box>
            <Grid container spacing={2}>
              {recentRecipes.map((recipe) => (
                <Grid item xs={12} sm={6} lg={4} key={recipe.id}>
                  <RecipeCard recipe={recipe} />
                </Grid>
              ))}
            </Grid>
          </Box>
        </>
      )}
    </Box>
  );
}
